import { useMemo, useState, useEffect } from 'react';
import { Dimensions, ScaledSize, Platform } from 'react-native';
import DeviceInfo from 'react-native-device-info';

/**
 * Device type hook that reactively updates when device dimensions change
 * 
 * Uses DeviceInfo.isTablet() as the authoritative source for tablet detection.
 * Re-checks tablet status when screen dimensions change (useful for foldable devices).
 * 
 * Note: The hook trusts DeviceInfo.isTablet() rather than using screen width alone,
 * as modern phones can have widths >= 600px and would be incorrectly flagged as tablets.
 * 
 * Only runs on native platforms (iOS/Android), not on web.
 * 
 * @returns {Object} Object with isTablet boolean that updates reactively
 */
export const useDeviceType = () => {
  // Early return for web platform - don't run device type detection on web
  if (Platform.OS === 'web') {
    const screenDimensions = Dimensions.get('window');
    const isTablet = screenDimensions.width >= 600;
    return { isTablet };
  }

  // Get initial tablet state from DeviceInfo
  const [deviceIsTablet, setDeviceIsTablet] = useState(() => DeviceInfo.isTablet());
  const [screenDimensions, setScreenDimensions] = useState<ScaledSize>(() => 
    Dimensions.get('window')
  );

  // Listen to dimension changes (critical for folding devices)
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      const prevWidth = screenDimensions.width;
      const prevHeight = screenDimensions.height;
      const prevIsTablet = deviceIsTablet;
      
      setScreenDimensions(window);
      // Re-check tablet status when dimensions change
      // For folding devices, screen width changes significantly
      const newDeviceIsTablet = DeviceInfo.isTablet();
      setDeviceIsTablet(newDeviceIsTablet);
      
      // Only log significant dimension changes (more than 50px) or tablet status changes
      // This reduces console noise from minor dimension adjustments
      const widthDelta = Math.abs(window.width - prevWidth);
      const heightDelta = Math.abs(window.height - prevHeight);
      const significantChange = widthDelta > 50 || heightDelta > 50 || prevIsTablet !== newDeviceIsTablet;

      if (significantChange && (prevWidth !== window.width || prevHeight !== window.height || prevIsTablet !== newDeviceIsTablet)) {
        // Only log in development mode
        if (__DEV__) {
          console.warn('📱 [useDeviceType] Significant dimension/tablet status change:', {
            dimensions: {
              prev: { width: prevWidth, height: prevHeight },
              new: { width: window.width, height: window.height },
              delta: { width: window.width - prevWidth, height: window.height - prevHeight }
            },
            tabletStatus: {
              prev: prevIsTablet,
              new: newDeviceIsTablet,
              changed: prevIsTablet !== newDeviceIsTablet
            }
          });
        }
      }
    });

    return () => {
      subscription?.remove();
    };
  }, [screenDimensions, deviceIsTablet]);

  // Determine tablet status based on device info
  // Trust DeviceInfo.isTablet() as the authoritative source
  // The fallback width check was too aggressive and incorrectly flagged phones as tablets
  const isTablet = useMemo(() => {
    // Use device info as the only source - it correctly identifies phones vs tablets
    // Modern phones can have widths >= 600px, so width alone is not a reliable indicator
    return deviceIsTablet;
  }, [deviceIsTablet]);

  return { isTablet };
};
